import {
  ActionGroup,
  Alert,
  AlertActionCloseButton,
  Button,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  TextInput,
} from '@patternfly/react-core';
import {ExclamationCircleIcon} from '@patternfly/react-icons';
import {useEffect, useState} from 'react';
import Conditional from 'src/components/empty/Conditional';
import {useAuthorizedEmails} from 'src/hooks/UseAuthorizedEmails';
import {NotificationEvent} from 'src/hooks/UseEvents';
import {NotificationMethod} from 'src/hooks/UseNotificationMethods';
import {useUpdateNotifications} from 'src/hooks/UseUpdateNotifications';
import {isValidEmail} from 'src/libs/utils';
import {fetchAuthorizedEmail} from 'src/resources/AuthorizedEmailResource';
import {NotificationEventConfig} from 'src/hooks/UseEvents';

export default function CreateEmailNotification(
  props: CreateEmailNotification,
) {
  const [title, setTitle] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isEmailAuthModalOpen, setIsEmailAuthModalOpen] = useState<boolean>();
  const {
    create,
    successCreatingNotification,
    errorCreatingNotification,
    resetCreatingNotification,
  } = useUpdateNotifications(props.org, props.repo);
  const {
    emailConfirmed,
    resetEmailConfirmed,
    polling,
    startPolling,
    stopPolling,
    errorPolling,
    sendAuthorizedEmail,
    successSendingAuthorizedEmail,
    errorSendingAuthorizedEmail,
    resetSendAuthorizationEmail,
  } = useAuthorizedEmails(props.org, props.repo);

  const isFormComplete =
    props.method != undefined &&
    props.event != undefined &&
    email != undefined &&
    email != '' &&
    props.isValidateConfig() &&
    isValidEmail(email);

  useEffect(() => {
    if (successSendingAuthorizedEmail) {
      startPolling(email);
      resetSendAuthorizationEmail();
      setIsEmailAuthModalOpen(false);
    }
  }, [successSendingAuthorizedEmail]);

  useEffect(() => {
    if (emailConfirmed) {
      create({
        config: {
          email: email,
        },
        event: props.event?.type,
        event_config: props.eventConfig,
        method: props.method?.type,
        title: title,
      });
      resetEmailConfirmed();
    }
  }, [emailConfirmed]);

  useEffect(() => {
    if (successCreatingNotification) {
      resetCreatingNotification();
      props.closeDrawer();
    }
  }, [successCreatingNotification]);

  useEffect(() => {
    if (errorCreatingNotification) {
      props.setError('Unable to create notification');
      resetCreatingNotification();
    }
  }, [errorCreatingNotification]);

  const createNotification = async () => {
    let emailStatus = null;
    try {
      emailStatus = await fetchAuthorizedEmail(props.org, props.repo, email);
    } catch (err) {
      props.setError('Unable to verify email');
    }
    if (emailStatus != null && emailStatus.confirmed) {
      create({
        config: {
          email: email,
        },
        event: props.event?.type,
        event_config: props.eventConfig,
        method: props.method?.type,
        title: title,
      });
    } else {
      setIsEmailAuthModalOpen(true);
    }
  };

  return (
    <>
      <Modal
        variant={ModalVariant.small}
        isOpen={isEmailAuthModalOpen}
        onClose={() => setIsEmailAuthModalOpen(false)}
      >
        <ModalHeader title="Email Authorization" />
        <ModalBody>
          <Conditional if={errorSendingAuthorizedEmail}>
            <Alert
              isInline
              actionClose={
                <AlertActionCloseButton onClose={resetSendAuthorizationEmail} />
              }
              variant="danger"
              title="Failure sending authorized email"
            />
          </Conditional>
          The email address {email} has not been authorized to receive
          notifications from this repository. Please click &lsquo;Send
          Authorized Email&rsquo; to start the authorization process.
        </ModalBody>
        <ModalFooter>
          <Button
            key="sendemail"
            variant="primary"
            data-testid="send-authorized-email-btn"
            onClick={() => sendAuthorizedEmail(email)}
          >
            Send Authorized Email
          </Button>
          <Button
            key="cancel"
            variant="link"
            onClick={() => setIsEmailAuthModalOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        variant={ModalVariant.small}
        isOpen={polling}
        onClose={stopPolling}
      >
        <ModalHeader title="Email Authorization" />
        <ModalBody>
          An email has been sent to {email}. Please click the link contained in
          the email.
        </ModalBody>
        <ModalFooter>
          <Button key="cancel" variant="primary" onClick={stopPolling}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        variant={ModalVariant.small}
        isOpen={errorPolling}
        onClose={stopPolling}
      >
        <ModalHeader title="Email Authorization" />
        <ModalBody>
          Unable to verify email confirmation. Please wait a moment and retry.
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => startPolling(email)}>
            Retry
          </Button>
          <Button variant="link" onClick={stopPolling}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <FormGroup fieldId="email" label="E-mail address" isRequired>
        <TextInput
          id="notification-email"
          data-testid="notification-email"
          isRequired
          value={email}
          onChange={(_event, value) => setEmail(value)}
        />

        {!isValidEmail(email) && (
          <FormHelperText>
            <HelperText>
              <HelperTextItem variant="error" icon={<ExclamationCircleIcon />}>
                Invalid email
              </HelperTextItem>
            </HelperText>
          </FormHelperText>
        )}
      </FormGroup>
      <FormGroup fieldId="title" label="Title">
        <TextInput
          id="notification-title"
          data-testid="notification-title"
          value={title}
          onChange={(_event, value) => setTitle(value)}
        />
      </FormGroup>
      <ActionGroup>
        <Button
          data-testid="notification-submit-btn"
          isDisabled={!isFormComplete}
          onClick={createNotification}
          variant="primary"
        >
          Submit
        </Button>
      </ActionGroup>
    </>
  );
}

interface CreateEmailNotification {
  org: string;
  repo: string;
  event: NotificationEvent;
  method: NotificationMethod;
  eventConfig: NotificationEventConfig;
  isValidateConfig: () => boolean;
  closeDrawer: () => void;
  setError: (error: string) => void;
}
